import { useAppSelector } from '../slices/commonSelectors';
import { DEMO_ORG_FLAGS } from '../environment';

const POWER_OUTAGES = 'POWER_OUTAGES' as const;
const OFFLINE_ENVIRONMENT = 'OFFLINE_ENVIRONMENT' as const;
const STREAMLINE_PLATFORM = 'STREAMLINE_PLATFORM' as const;
const NOTIFICATION_JOBS = 'NOTIFICATION_JOBS' as const;
const SHOW_FAULTY_EVENT_HAZARD_CATEGORIES =
  'SHOW_FAULTY_EVENT_HAZARD_CATEGORIES' as const;
const NEO = 'NEO' as const;
const VELCO = 'VELCO' as const;
const SITREP_LANDING = 'SITREP_LANDING' as const;
const DGPT_ALERTS = 'DGPT_ALERTS' as const;
const EMW_DGPT = 'EMW_DGPT' as const;
const HIGH_SECURITY = 'HIGH_SECURITY' as const;
const TENNESSEE_NATIONAL_GUARD = 'TENNESSEE_NATIONAL_GUARD' as const;
const SITREP_COLLAB = 'SITREP_COLLAB' as const;
const CFA = 'CFA' as const;
const EPA = 'EPA' as const;
const DHS = 'DHS' as const;
const CNIC = 'CNIC' as const;
const GOVERNMENT_OF_ONTARIO = 'GOVERNMENT_OF_ONTARIO' as const;
const EQUINOR = 'EQUINOR' as const;
const JAPAN_DEMO = 'JAPAN_DEMO' as const;
const NOTIFICATION_DASHBOARD = 'NOTIFICATION_DASHBOARD' as const;
const AI_CHAT_V2 = 'AI_CHAT_V2' as const;
const ACTION_TICKETS = 'ACTION_TICKETS' as const;
const NOTIFICATION_CENTER = 'NOTIFICATION_CENTER' as const;
const HWCG = 'HWCG' as const;
const PREMIUM_RESOURCE_MANAGEMENT = 'PREMIUM_RESOURCE_MANAGEMENT' as const;
const AOR_TAB = 'AOR_TAB' as const;
const POSITION_WORKSPACES = 'POSITION_WORKSPACES' as const;
const FEMA_OPS_BRIEFING = 'FEMA_OPS_BRIEFING' as const;
const REPORT_VERSIONING = 'REPORT_VERSIONING' as const;
const ATTRIBUTE_AUTOMATION_RULE_CONDITIONS =
  'ATTRIBUTE_AUTOMATION_RULE_CONDITIONS' as const;
const CREATE_REPORT_TEMPLATES = 'CREATE_REPORT_TEMPLATES' as const;
const PRIVATE_SECTOR = 'PRIVATE_SECTOR' as const;
const HUB_ONLY = 'HUB_ONLY' as const;
const USCG_FOC_REQUIREMENTS = 'USCG_FOC_REQUIREMENTS' as const;
const VDEM = 'VDEM' as const;

/** Stored organization name that starts with Hub Only turned on. */
export const HUB_ONLY_ORG_NAME = 'Chick-fil-A';

/** Stored organization name that always has the HWCG flag on. */
export const HWCG_ORG_NAME = 'HWCG';

/** Stored organization name that always has USCG FOC Requirements on. */
export const USCG_FOC_ORG_NAME = 'United States Coast Guard';

export const ALL_FEATURE_FLAGS = [
  POWER_OUTAGES,
  OFFLINE_ENVIRONMENT,
  STREAMLINE_PLATFORM,
  NOTIFICATION_JOBS,
  SHOW_FAULTY_EVENT_HAZARD_CATEGORIES,
  NEO,
  VELCO,
  SITREP_LANDING,
  DGPT_ALERTS,
  EMW_DGPT,
  HIGH_SECURITY,
  TENNESSEE_NATIONAL_GUARD,
  SITREP_COLLAB,
  CFA,
  EPA,
  DHS,
  CNIC,
  GOVERNMENT_OF_ONTARIO,
  EQUINOR,
  JAPAN_DEMO,
  NOTIFICATION_DASHBOARD,
  AI_CHAT_V2,
  ACTION_TICKETS,
  NOTIFICATION_CENTER,
  HWCG,
  PREMIUM_RESOURCE_MANAGEMENT,
  AOR_TAB,
  POSITION_WORKSPACES,
  FEMA_OPS_BRIEFING,
  REPORT_VERSIONING,
  ATTRIBUTE_AUTOMATION_RULE_CONDITIONS,
  CREATE_REPORT_TEMPLATES,
  PRIVATE_SECTOR,
  HUB_ONLY,
  USCG_FOC_REQUIREMENTS,
  VDEM,
] as const;

export type FeatureFlag = (typeof ALL_FEATURE_FLAGS)[number];

/**
 * Optional human labels for the /feature_flags page (falls back to the
 * constant).
 */
export const FEATURE_FLAG_LABELS: Partial<Record<FeatureFlag, string>> = {
  TENNESSEE_NATIONAL_GUARD: 'Tennessee National Guard',
  SITREP_COLLAB: 'Sitrep concurrent editing',
  CFA: 'Chick-fil-A',
  EPA: 'EPA demo',
  DHS: 'DHS demo',
  CNIC: 'CNIC demo',
  GOVERNMENT_OF_ONTARIO: 'Government of Ontario',
  EQUINOR: 'Equinor demo',
  JAPAN_DEMO: 'Japan Demo',
  NOTIFICATION_DASHBOARD: 'Notification sends dashboard',
  AI_CHAT_V2: 'PRATUS AI drawer (modern shell)',
  ACTION_TICKETS: 'Action tickets (detail layout + fields)',
  NOTIFICATION_CENTER: 'Notification center (top bar bell + tab badges)',
  HWCG: 'HWCG (roster position phase tabs)',
  PREMIUM_RESOURCE_MANAGEMENT: 'Premium Resource Management',
  AOR_TAB: 'AOR Tab',
  POSITION_WORKSPACES: 'Position workspaces',
  FEMA_OPS_BRIEFING: 'FEMA daily operations briefing',
  REPORT_VERSIONING: 'Report versioning',
  ATTRIBUTE_AUTOMATION_RULE_CONDITIONS: 'Attribute Automation Rule Conditions',
  CREATE_REPORT_TEMPLATES: 'Create report templates',
  PRIVATE_SECTOR: 'Private Sector',
  HUB_ONLY: 'Hub Only',
  USCG_FOC_REQUIREMENTS: 'USCG FOC Requirements',
  VDEM: 'VDEM',
};

/**
 * Flags that rebrand the whole product for one customer.
 *
 * These are the same eight that carry a label above, and they are different in
 * kind from the rest: turning one on renames the organisation, restyles the
 * chrome and seeds demo data. An org record or a deployment env that happens to
 * list one puts that customer's branding in front of everyone in the
 * environment, which is why the two automatic sources below filter them out.
 */
export const CUSTOMER_DEMO_FLAGS: readonly FeatureFlag[] = [
  CFA,
  EPA,
  DHS,
  CNIC,
  GOVERNMENT_OF_ONTARIO,
  EQUINOR,
  JAPAN_DEMO,
  TENNESSEE_NATIONAL_GUARD,
];

const DEMO_FLAG_SET: ReadonlySet<string> = new Set(CUSTOMER_DEMO_FLAGS);

/**
 * Strips demo flags from a list that is being merged in automatically.
 *
 * Applied to the group's `default_settings.active_flags` and to the
 * `DEFAULT_FEATURE_FLAGS` environment variable, and deliberately _not_ to the
 * /feature_flags page: a demo still has to be switchable on for a session, it
 * just must not switch itself on.
 */
export function withoutAutoEnabledDemoFlags<T extends string>(
  flags: readonly T[]
): T[] {
  return flags.filter((flag) => !DEMO_FLAG_SET.has(flag));
}

/**
 * True only where the deployment exists to show the product to prospects, and
 * each organisation therefore belongs to one customer and is meant to carry
 * that customer's branding.
 */
export const orgDemoFlagsAllowed = (): boolean => DEMO_ORG_FLAGS === 'true';

/**
 * Merge flags that came from the organisation record.
 *
 * Without `DEMO_ORG_FLAGS` this is `withoutAutoEnabledDemoFlags`, so an org
 * that happens to list a customer demo flag cannot rebrand the product for
 * whoever opens it. Where the variable is set the org _is_ the customer, and a
 * prospect cannot be asked to tick a box on /feature_flags before their own
 * branding appears, so the flags are taken as given.
 *
 * Deliberately scoped to the per-org source. `DEFAULT_FEATURE_FLAGS` stays
 * filtered even there, because it is deployment-wide and would brand every org
 * in the environment at once.
 */
export function mergeOrgFeatureFlags<T extends string>(
  flags: readonly T[]
): T[] {
  return orgDemoFlagsAllowed()
    ? [...flags]
    : withoutAutoEnabledDemoFlags(flags);
}

export const useFeatureFlag = (flag: FeatureFlag) => {
  // Flags arrive with the group; treat "not loaded yet" (and partial story
  // stores) as all-off rather than crashing the consumer.
  const flags = useAppSelector((s) => s.app.featureFlags) as
    | FeatureFlag[]
    | undefined;
  return flags?.includes(flag) ?? false;
};

export const usePowerOutagesFlag = () => {
  return useFeatureFlag(POWER_OUTAGES);
};
export const useOfflineEnvironmentFlag = () => {
  return useFeatureFlag(OFFLINE_ENVIRONMENT);
};
export const useStreamlinePlatformFlag = () => {
  return useFeatureFlag(STREAMLINE_PLATFORM);
};
export const useNotificationJobsFlag = () => {
  return useFeatureFlag(NOTIFICATION_JOBS);
};
export const useShowFaultyHazardEventCategoriesFlag = () => {
  return useFeatureFlag(SHOW_FAULTY_EVENT_HAZARD_CATEGORIES);
};
export const useNeoFlag = () => {
  return useFeatureFlag(NEO);
};
export const useVelcoFlag = () => {
  return useFeatureFlag(VELCO);
};
export const useSitrepLandingFlag = () => {
  return useFeatureFlag(SITREP_LANDING);
};
export const useDgptAlertsFlag = () => {
  return useFeatureFlag(DGPT_ALERTS);
};
export const useEmwDgptFlag = () => {
  return useFeatureFlag(EMW_DGPT);
};
export const useHighSecurityFlag = () => {
  return useFeatureFlag(HIGH_SECURITY);
};
export const useTennesseeNationalGuardFlag = () => {
  return useFeatureFlag(TENNESSEE_NATIONAL_GUARD);
};
export const useSitrepCollabFlag = () => {
  return useFeatureFlag(SITREP_COLLAB);
};
export const useCfaFlag = () => {
  return useFeatureFlag(CFA);
};
export const useEpaFlag = () => {
  return useFeatureFlag(EPA);
};
export const useDhsFlag = () => {
  return useFeatureFlag(DHS);
};
export const useCnicFlag = () => {
  return useFeatureFlag(CNIC);
};
export const useGovernmentOfOntarioFlag = () => {
  return useFeatureFlag(GOVERNMENT_OF_ONTARIO);
};
export const useEquinorFlag = () => {
  return useFeatureFlag(EQUINOR);
};
export const useJapanDemoFlag = () => {
  return useFeatureFlag(JAPAN_DEMO);
};

export type TropicalDemoKind =
  | 'ontario'
  | 'equinor'
  | 'japan'
  | 'cnic'
  | 'dhs'
  | 'epa'
  | 'cfa'
  | null;

/**
 * Ontario / Equinor / CNIC / DHS / EPA replace the CFA Helene demo when those
 * flags are on. Only one demo can be active, so the first match wins.
 */
export const resolveTropicalDemoKind = ({
  ontario,
  equinor,
  japan,
  cnic,
  dhs,
  epa,
  cfa,
}: {
  ontario?: boolean;
  equinor?: boolean;
  japan?: boolean;
  cnic?: boolean;
  dhs?: boolean;
  epa?: boolean;
  cfa?: boolean;
}): TropicalDemoKind => {
  if (ontario) return 'ontario';
  if (equinor) return 'equinor';
  if (japan) return 'japan';
  if (cnic) return 'cnic';
  if (dhs) return 'dhs';
  if (epa) return 'epa';
  if (cfa) return 'cfa';
  return null;
};

/**
 * Equinor is not a tropical scenario at all — it is the Grane offshore spill —
 * but it reuses this demo pipeline, so it resolves here rather than growing a
 * second parallel one.
 */
export const useTropicalDemoKind = (): TropicalDemoKind =>
  resolveTropicalDemoKind({
    ontario: useGovernmentOfOntarioFlag(),
    equinor: useEquinorFlag(),
    japan: useJapanDemoFlag(),
    cnic: useCnicFlag(),
    dhs: useDhsFlag(),
    epa: useEpaFlag(),
    cfa: useCfaFlag(),
  });

/**
 * Demos that render as a roster of fixed sites with coordinates and threat
 * tags. These share one content shape, so they share the render path and differ
 * only by the pack returned from `getFacilityDemoPack`.
 */
export const isFacilityDemoKind = (
  kind: TropicalDemoKind
): kind is 'dhs' | 'equinor' | 'japan' | 'ontario' | 'cnic' =>
  kind === 'dhs' ||
  kind === 'equinor' ||
  kind === 'japan' ||
  kind === 'ontario' ||
  kind === 'cnic';

export const useCfaDemoEnabled = () => {
  return useTropicalDemoKind() === 'cfa';
};

/** TNG flood/outage Memphis demo — not used when CFA / EPA / DHS is on. */
export const useTennesseeMemphisDemoEnabled = () => {
  const tng = useTennesseeNationalGuardFlag();
  const demoKind = useTropicalDemoKind();
  return tng && demoKind === null;
};

/** Dark shadcn header / notifications chrome. Platform default, not a demo flag. */
export const useHubChromeEnabled = () => {
  return true;
};

/**
 * Deliberately not coupled to CFA. The delivery report was gated behind that
 * customer flag while it was being proven out, which meant no other
 * organisation could see who received their notifications.
 */
export const useNotificationDashboardFlag = () => {
  return useFeatureFlag(NOTIFICATION_DASHBOARD);
};

/**
 * Modern PRATUS AI drawer (`components/DisasterGPT/chatV2`). On by default; the
 * Feature Flags page can still persist `AI_CHAT_V2` on the org. State/API
 * contract is shared, so flipping the flag never changes what is sent to COMS.
 */
export const useAiChatV2Flag = () => {
  // Default ON. Group `active_flags` is opt-in and will not include a newly
  // added flag, so a presence-check would keep the legacy shell.
  return true;
};

/**
 * Ticket layout for org / incident actions, plus the work-assignment style
 * fields (reporting location, requested arrival, resource requirements,
 * hazards, attachments) and the roster position task list. On by default; the
 * Feature Flags page can still persist `ACTION_TICKETS` on the org. Reads and
 * writes carry the new fields either way, so flipping the flag never loses what
 * was entered.
 */
export const useActionTicketsFlag = () => {
  // Default ON. Group `active_flags` is opt-in and will not include a newly
  // added flag, so a presence-check would keep the stacked action form.
  return true;
};

export const useNotificationCenterFlag = () => {
  return useFeatureFlag(NOTIFICATION_CENTER);
};

/**
 * Splits a roster seat into incident-lifecycle phases: the position modal gains
 * Team / Activation / Planning / Execution tabs, and the seat itself becomes
 * clickable rather than pencil-only.
 */
export const useHwcgFlag = () => {
  return useFeatureFlag(HWCG);
};

/**
 * ICS 211 / 238 / 221 / 213RR surfaces and the Resource Requests board. On by
 * default. The Feature Flags page can still persist
 * `PREMIUM_RESOURCE_MANAGEMENT` on the org.
 */
export const usePremiumResourceManagementFlag = () => {
  // Default ON. Group `active_flags` is opt-in and will not include a newly
  // added flag, so a presence-check would keep the board and forms hidden.
  return true;
};

/**
 * Hub More-menu AORs tab. Off by default. Org-settings AOR create/edit and map
 * AOR layers stay available either way.
 */
export const useAorTabFlag = () => {
  return useFeatureFlag(AOR_TAB);
};

/**
 * Position and section homes inside an incident. On by default. Planning-P and
 * the incident Tasks page stay available either way. The Feature Flags page can
 * still persist `POSITION_WORKSPACES` on the org.
 */
export const usePositionWorkspacesFlag = () => {
  // Default ON. Group `active_flags` is opt-in and will not include a newly
  // added flag, so a presence-check would keep the homes hidden.
  return true;
};

/**
 * One card per report, with the ICS form Versions menu. Off unless the org
 * turns on Report versioning. The Current / Historical / Drafts tabs stay
 * otherwise.
 */
export const useReportVersioningFlag = () => {
  return useFeatureFlag(REPORT_VERSIONING);
};

/** When on, the Hub keeps a FEMA daily operations briefing presentation. */
export const useFemaOpsBriefingFlag = () => {
  return useFeatureFlag(FEMA_OPS_BRIEFING);
};

/**
 * Virginia VEST roster template. Off unless the org turns on VDEM. Does not
 * rebrand the product.
 */
export const useVdemFlag = () => {
  return useFeatureFlag(VDEM);
};

/**
 * Attribute conditions in the automation rule editor. Off unless the org turns
 * on Attribute Automation Rule Conditions.
 */
export const useAttributeAutomationRuleConditionsFlag = () => {
  return useFeatureFlag(ATTRIBUTE_AUTOMATION_RULE_CONDITIONS);
};

/**
 * Blank and cloned report templates in Create Draft Report. On by default via
 * the initial feature-flag list, so a new org does not have to opt in. The
 * Feature Flags page can turn `CREATE_REPORT_TEMPLATES` off for the session.
 */
export const useCreateReportTemplatesFlag = () => {
  return useFeatureFlag(CREATE_REPORT_TEMPLATES);
};

/** Renames AOR / AORs to Business Unit / Business Units. Off by default. */
export const usePrivateSectorFlag = () => {
  return useFeatureFlag(PRIVATE_SECTOR);
};

export function isPrivateSectorEnabled(
  flags: readonly string[] | undefined
): boolean {
  return flags?.includes(PRIVATE_SECTOR) ?? false;
}

/**
 * Hub Only starts on for Chick-fil-A. Other organizations stay off until the
 * flag is turned on. Does not change the list it was given.
 */
export function withHubOnlyDefault<T extends string>(
  flags: readonly T[],
  groupName: string | undefined
): T[] {
  if (groupName !== HUB_ONLY_ORG_NAME || flags.includes(HUB_ONLY as T)) {
    return [...flags];
  }
  return [...flags, HUB_ONLY as T];
}

/**
 * HWCG stays on for the organization named HWCG. Other organizations stay off
 * until the flag is turned on. Does not change the list it was given.
 */
export function withHwcgDefault<T extends string>(
  flags: readonly T[],
  groupName: string | undefined
): T[] {
  if (groupName !== HWCG_ORG_NAME || flags.includes(HWCG as T)) {
    return [...flags];
  }
  return [...flags, HWCG as T];
}

/**
 * USCG FOC Requirements stays on for United States Coast Guard. Other
 * organizations stay off until the flag is turned on. Does not change the list
 * it was given.
 */
export function withUscgFocRequirementsDefault<T extends string>(
  flags: readonly T[],
  groupName: string | undefined
): T[] {
  if (
    groupName !== USCG_FOC_ORG_NAME ||
    flags.includes(USCG_FOC_REQUIREMENTS as T)
  ) {
    return [...flags];
  }
  return [...flags, USCG_FOC_REQUIREMENTS as T];
}

/**
 * USCG FOC Requirements implies Position Workspaces so FOC orgs persist and
 * show both on /feature_flags. Does not remove Position Workspaces when FOC is
 * off.
 */
export function withPositionWorkspacesWhenFoc<T extends string>(
  flags: readonly T[]
): T[] {
  if (
    !flags.includes(USCG_FOC_REQUIREMENTS as T) ||
    flags.includes(POSITION_WORKSPACES as T)
  ) {
    return [...flags];
  }
  return [...flags, POSITION_WORKSPACES as T];
}

/** Locks the incident lifecycle tour and Incident Workspace. */
export const useHubOnlyFlag = () => {
  return useFeatureFlag(HUB_ONLY);
};

/**
 * Coast Guard FOC requirement walkthroughs. Always on for the United States
 * Coast Guard organization; otherwise off until the org turns on USCG FOC
 * Requirements. Does not rebrand the product.
 */
export const useUscgFocRequirementsFlag = () => {
  return useFeatureFlag(USCG_FOC_REQUIREMENTS);
};
